import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { GetTestInstruments, DeleteTestInstrument } from "../../../../services/TestLibService";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddTestInstrumentDialog from "./AddTestInstrumentDialog"; // Import the AddTestInstrumentDialog component
import CommonLoader from "../../../common/CommonLoader";
import ViewTestInstrument from "./ViewTestInstrument"; // Import the ViewTestInstrument component

const TestInstrumentsTable = () => {
  const columns = ["Machine ID", "Name", "Actions"];
  const columnHeaders = ["machineId", "name"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [instrumentsData, setInstrumentsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isAddInstrumentDialogOpen, setIsAddInstrumentDialogOpen] = useState(false);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        setLoading(true);
        const response = await GetTestInstruments();
        setInstrumentsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching instruments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstruments();
  }, []);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        setLoading(true);
        const response = await GetTestInstruments();
        setInstrumentsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching instruments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstruments();
  }, [isDeleted]);

  useEffect(() => {
    if (!instrumentsData) {
      return;
    }

    const filteredResults = instrumentsData.filter((instrument) =>
      columnHeaders.some((header) =>
        instrument[header]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, instrumentsData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (instrument) => {
    setSelectedInstrument(instrument);
  };

  const handleBackClick = () => {
    setSelectedInstrument(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (instrument) => {
    setSelectedInstrument(instrument.machineId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedInstrument(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteTestInstrument(selectedInstrument);
      if (response.results === true) {
        NotificationManager.success("Test instrument deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedInstrument(null);
      } else {
        NotificationManager.error("Error deleting test instrument");
      }
    } catch (error) {
      NotificationManager.error("Error deleting test instrument");
    }
  };

  const handleAddInstrumentClick = () => {
    setSelectedInstrument(null);
    setIsAddInstrumentDialogOpen(true);
  };

  const handleEditInstrumentClick = (instrument) => {
    setSelectedInstrument(instrument);
    setIsAddInstrumentDialogOpen(true);
  };

  const handleAddInstrumentDialogClose = () => {
    setIsAddInstrumentDialogOpen(false);
    setSelectedInstrument(null);
  };

  const handleAddInstrumentDialogSuccess = () => {
    setIsAddInstrumentDialogOpen(false);
    setSelectedInstrument(null);
    setIsDeleted(!isDeleted);
  };

  const renderActions = (instrument) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditInstrumentClick(instrument)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(instrument)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(instrument)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Test Instrumens Management </span>
    <hr />
      {selectedInstrument && !isAddInstrumentDialogOpen && !isDeleteConfirmationOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewTestInstrument instrument={selectedInstrument} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddInstrumentClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column}
                        onClick={() => columnHeaders[index] && handleRequestSort(columnHeaders[index])}
                        sx={{ cursor: columnHeaders[index] ? "pointer" : "default" }}
                      >
                        {columnHeaders[index] ? (
                          <TableSortLabel
                            active={orderBy === columnHeaders[index]}
                            direction={orderBy === columnHeaders[index] ? order : "asc"}
                            sx={{
                              color: "white !important",
                              "& .MuiTableSortLabel-icon": { color: "white !important" },
                            }}
                          >
                            {column}
                          </TableSortLabel>
                        ) : (
                          column
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((instrument, index) => (
                      <TableRow
                        key={instrument.machineId}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{instrument[column]}</TableCell>
                        ))}
                        <TableCell>{renderActions(instrument)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this instrument?"
            color="red"
          />
        </>
      )}

      {isAddInstrumentDialogOpen && (
        <AddTestInstrumentDialog
          open={isAddInstrumentDialogOpen}
          onClose={handleAddInstrumentDialogClose}
          onSave={handleAddInstrumentDialogSuccess}
          selectedInstrument={selectedInstrument}
        />
      )}
    </>
  );
};

export default TestInstrumentsTable;
